import { AllOrganizationalRoles, Role } from './identity-contracts';

export const AuthorizableAction = {
    DISPLAY: 'DISPLAY',
    LIST: 'LIST',
    CREATE: 'CREATE',
    EDIT: 'EDIT',
    DELETE: 'DELETE',
    USE: 'USE',
    ROTATE: 'ROTATE',
    GRANT: 'GRANT',
    REVOKE: 'REVOKE',
    TAKE_PAYMENT: 'TAKE_PAYMENT',
    VOID_PAYMENT: 'VOID_PAYMENT',
    REFUND_PAYMENT: 'REFUND_PAYMENT',
    OVERRIDE_FEE: 'OVERRIDE_FEE',
    IMPERSONATE: 'IMPERSONATE',
    AUDIT: 'AUDIT',
} as const;
export type AuthorizableAction = keyof typeof AuthorizableAction;

export const ALL_AUTHORIZABLE_ACTIONS: AuthorizableAction[] = Object.values(AuthorizableAction);
export function isAuthorizableAction(action: string): action is AuthorizableAction {
    for (const validAction of ALL_AUTHORIZABLE_ACTIONS) {
        if (validAction === action) {
            return true;
        }
    }
    return false;
}

export const ResourceType = {
    ORGANIZATION: 'organization',
    USER: 'user',
    CREDENTIAL: 'credential',
    ORGANIZATION_CREDENTIAL: 'organization_credential',
    ROLE_ASSIGNMENT_CREDENTIAL: 'role_assignment_credential',
    ORGANIZATION_IPX_CREDENTIAL_POLICY: 'organization_ipx_credential_policy',
    IPX_CREDENTIAL_DEFINITION: 'ipx_credential_definition',
    IPX_WEB_RESOURCE_MODEL: 'ipx_web_resource_model',
    ALLOWED_WEBSITE_TEMPLATE: 'allowed_website_template',
    PERMISSIONS_BUNDLE: 'permissions_bundle',
    PERMISSION_POLICY: 'permission_policy',
    ROLE_ASSIGNMENT: 'role_assignment',
    RPA_MODULE: 'rpa_module',
    IPW: 'ipw',
    IPG: 'ipg',
    PROCESSOR: 'processor',
    MERCHANT_ACCOUNT: 'merchant_account',
    LOGICAL_TERMINAL: 'logical_terminal',
    TERMINAL_DEVICE: 'terminal_device',
    PAYMENT_TRANSACTION: 'payment_transaction',
    PAYMENT_REQUEST: 'payment_request',
    PAYMENT_METHOD_TOKEN: 'payment_method_token',
} as const;

export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];
export const ALL_RESOURCE_TYPES: ResourceType[] = Object.values(ResourceType);
export function isResourceType(resourceType: string): resourceType is ResourceType {
    for (const validResourceType of ALL_RESOURCE_TYPES) {
        if (validResourceType === resourceType) {
            return true;
        }
    }
    return false;
}

export const ResourceTypeActions = {
    [ResourceType.ORGANIZATION]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.USER]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.IMPERSONATE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.CREDENTIAL]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.USE,
        AuthorizableAction.ROTATE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.ORGANIZATION_CREDENTIAL]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.USE,
        AuthorizableAction.ROTATE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.ROLE_ASSIGNMENT_CREDENTIAL]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.USE,
        AuthorizableAction.ROTATE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY]: [
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.IPX_CREDENTIAL_DEFINITION]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.IPX_WEB_RESOURCE_MODEL]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.ALLOWED_WEBSITE_TEMPLATE]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
        AuthorizableAction.USE,
    ],
    [ResourceType.PERMISSIONS_BUNDLE]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.PERMISSION_POLICY]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.GRANT,
        AuthorizableAction.REVOKE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.ROLE_ASSIGNMENT]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.GRANT,
    AuthorizableAction.REVOKE,
        AuthorizableAction.IMPERSONATE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.RPA_MODULE]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.USE,
    ],
    [ResourceType.IPW]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.USE,
    ],
    [ResourceType.IPG]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.USE,
    ],
    [ResourceType.PROCESSOR]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.MERCHANT_ACCOUNT]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.USE,
        AuthorizableAction.TAKE_PAYMENT,
        AuthorizableAction.VOID_PAYMENT,
        AuthorizableAction.REFUND_PAYMENT,
        AuthorizableAction.OVERRIDE_FEE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.LOGICAL_TERMINAL]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.USE,
        AuthorizableAction.TAKE_PAYMENT,
        AuthorizableAction.VOID_PAYMENT,
        AuthorizableAction.REFUND_PAYMENT,
        AuthorizableAction.OVERRIDE_FEE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.TERMINAL_DEVICE]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.USE,
        AuthorizableAction.TAKE_PAYMENT,
        AuthorizableAction.VOID_PAYMENT,
        AuthorizableAction.REFUND_PAYMENT,
        AuthorizableAction.OVERRIDE_FEE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.PAYMENT_TRANSACTION]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.EDIT,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.PAYMENT_REQUEST]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.CREATE,
        AuthorizableAction.EDIT,
        AuthorizableAction.DELETE,
        AuthorizableAction.AUDIT,
    ],
    [ResourceType.PAYMENT_METHOD_TOKEN]: [
        AuthorizableAction.DISPLAY,
        AuthorizableAction.LIST,
        AuthorizableAction.DELETE,
    ],
} as const satisfies Record<ResourceType, readonly AuthorizableAction[]>;
export type AllowedActionForResourceType<T extends ResourceType> =
    (typeof ResourceTypeActions)[T][number];

export function getAllowedActionsForResourceType<T extends ResourceType>(
    resourceType: T
): readonly AllowedActionForResourceType<T>[] {
    return ResourceTypeActions[resourceType];
}

export function isAllowedActionForResourceType<T extends ResourceType>(
    resourceType: T,
    action: AuthorizableAction
): action is AllowedActionForResourceType<T> {
    const allowedActions: readonly AuthorizableAction[] =
        getAllowedActionsForResourceType(resourceType);
    return allowedActions.includes(action);
}

/**
 * Role Eligibility Matrix
 *
 * Defines which roles can be granted which actions
 * This is a hard gate - ineligible roles CANNOT be granted permission
 * even if admin tries to create the grant
 */

/**
 * Role eligibility matrix for secret actions
 *
 * Key: AuthorizableAction
 * Value: Array of roles that CAN be granted this action
 *
 * Transitional rules after removing capacity-specific org roles:
 * - INTERNAL_ADMIN retains broad admin visibility/edit eligibility
 * - PRINCIPAL and ADMIN retain org-admin secret-management eligibility
 * - REP retains minimal operational eligibility
 * - Capacity-specific constraints are enforced elsewhere via organization flags
 */
const AUTHORIZABLE_ACTION_ELIGIBILITY: Record<AuthorizableAction, Role[]> = {
    [AuthorizableAction.DISPLAY]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.LIST]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.CREATE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.EDIT]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.DELETE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.USE]: [Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.ROTATE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.GRANT]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL],
    [AuthorizableAction.REVOKE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL],
    [AuthorizableAction.TAKE_PAYMENT]: AllOrganizationalRoles,
    [AuthorizableAction.VOID_PAYMENT]: AllOrganizationalRoles,
    [AuthorizableAction.REFUND_PAYMENT]: AllOrganizationalRoles,
    [AuthorizableAction.OVERRIDE_FEE]: AllOrganizationalRoles,
    [AuthorizableAction.IMPERSONATE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL],
    [AuthorizableAction.AUDIT]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN],
} as const;

/**
 * Check if a role is eligible for an action
 *
 * @param action - Action being checked
 * @returns true if role can be granted this action
 *
 * Note: SUPERADMIN always returns true (handled in caller)
 */
export function getEligibleRolesForAction(action: AuthorizableAction): readonly Role[] {
    return AUTHORIZABLE_ACTION_ELIGIBILITY[action];
}

/**
 * Check if a role is eligible for an action
 *
 * @param role - User's role (from RoleAssignment)
 * @param action - Action being checked
 * @returns true if role can be granted this action
 *
 * Note: SUPERADMIN always returns true (handled in caller)
 */
export function isRoleEligibleForAction(role: Role, action: AuthorizableAction): boolean {
    return getEligibleRolesForAction(action).includes(role);
}
